import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { eventsService } from '../../services/eventsService.js';
import styles from './EventForm.module.css';

const GOOGLE_MAPS_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

// Lazy-load the Google Maps JS API (places library) once per page.
// Returns a promise that resolves when window.google.maps.places is ready.
let mapsLoader = null;
function loadGoogleMaps() {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.google?.maps?.places) return Promise.resolve();
  if (mapsLoader) return mapsLoader;
  if (!GOOGLE_MAPS_KEY) return Promise.reject(new Error('missing key'));
  mapsLoader = new Promise((resolve, reject) => {
    const cbName = `__gmapsReady_${Date.now()}`;
    window[cbName] = () => { delete window[cbName]; resolve(); };
    const s = document.createElement('script');
    s.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_KEY}&libraries=places&callback=${cbName}`;
    s.async = true;
    s.defer = true;
    s.onerror = () => { mapsLoader = null; reject(new Error('script load failed')); };
    document.head.appendChild(s);
  });
  return mapsLoader;
}

const EMPTY = {
  title: '',
  description: '',
  location: '',
  starts_at: '',
  ends_at: '',
  capacity: '',
  is_free: true,
  price_pence: '',
};

function toInputDatetime(iso) {
  if (!iso) return '';
  return iso.slice(0, 16);
}

export default function EventForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();

  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [event, setEvent] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const locationInputRef = useRef(null);

  // Wire Google Places Autocomplete to the location input. Falls back silently
  // to a plain text input if the key is missing or the script fails to load.
  useEffect(() => {
    if (!GOOGLE_MAPS_KEY) return;
    let autocomplete;
    let listener;
    let cancelled = false;
    loadGoogleMaps()
      .then(() => {
        if (cancelled || !locationInputRef.current || !window.google?.maps?.places) return;
        autocomplete = new window.google.maps.places.Autocomplete(locationInputRef.current, {
          fields: ['formatted_address', 'name'],
          // Bias toward UK for the Engine Room pilot; users can still pick anywhere.
          componentRestrictions: { country: ['gb'] },
        });
        listener = autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          // Prefer "Name, formatted_address" so the venue label survives, e.g.
          // "The Engine Room, 1 St James' Blvd, Newcastle upon Tyne NE1 4BN, UK".
          const value = place.name && place.formatted_address && !place.formatted_address.startsWith(place.name)
            ? `${place.name}, ${place.formatted_address}`
            : place.formatted_address || place.name || '';
          if (value) setForm((f) => ({ ...f, location: value }));
        });
      })
      .catch(() => { /* silently fall back to plain input */ });
    return () => {
      cancelled = true;
      if (listener) listener.remove();
      // Google injects a `.pac-container` dropdown into <body>; clean it up so
      // it doesn't linger when the user navigates away mid-typing.
      document.querySelectorAll('.pac-container').forEach((el) => el.remove());
    };
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    let active = true;
    (async () => {
      try {
        const ev = await eventsService.getEvent(id);
        if (!active) return;
        if (!ev) { toast.error('Event not found'); navigate('/events'); return; }
        setEvent(ev);
        setForm({
          title: ev.title || '',
          description: ev.description || '',
          location: ev.location || '',
          starts_at: toInputDatetime(ev.starts_at),
          ends_at: toInputDatetime(ev.ends_at),
          capacity: ev.capacity ?? '',
          is_free: ev.is_free ?? true,
          price_pence: ev.price_pence ?? '',
        });
      } catch (err) {
        if (active) { toast.error(err.message); navigate('/events'); }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [id, isEdit, navigate]);

  function set(field) {
    return (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  }
  function setCheck(field) {
    return (e) => setForm((f) => ({ ...f, [field]: e.target.checked }));
  }

  function validate() {
    if (!form.title.trim()) return 'Title is required';
    if (!form.starts_at) return 'Start time is required';
    if (!form.ends_at) return 'End time is required';
    if (new Date(form.ends_at) <= new Date(form.starts_at)) {
      return 'End time must be after start time';
    }
    const cap = parseInt(form.capacity, 10);
    if (!cap || cap < 1) return 'Capacity must be at least 1';
    if (!form.is_free) {
      const px = parseInt(form.price_pence, 10);
      if (!px || px < 1) return 'Paid events require a price (in pence)';
    }
    return null;
  }

  function payload() {
    return {
      title: form.title.trim(),
      description: form.description.trim() || null,
      location: form.location.trim() || null,
      starts_at: new Date(form.starts_at).toISOString(),
      ends_at: new Date(form.ends_at).toISOString(),
      capacity: parseInt(form.capacity, 10),
      is_free: form.is_free,
      price_pence: form.is_free ? null : parseInt(form.price_pence, 10),
    };
  }

  async function handleSave(e) {
    e.preventDefault();
    const err = validate();
    if (err) { setValidationError(err); return; }
    setValidationError(null);
    setSaving(true);
    try {
      const ev = isEdit
        ? await eventsService.updateEvent(id, payload())
        : await eventsService.createEvent(payload());
      toast.success(isEdit ? 'Event updated' : 'Event created');
      const evId = ev?.id || id;
      navigate(evId ? `/events/${evId}/edit` : '/events');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handlePublish() {
    const eventId = id || event?.id;
    if (!eventId) return;
    setPublishing(true);
    try {
      const ev = await eventsService.publishEvent(eventId);
      setEvent(ev);
      toast.success('Event published');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setPublishing(false);
    }
  }

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.inner}>
          <div className={styles.loadingWrap}>
            <div className={styles.spinner} aria-hidden="true" />
            <p className={styles.loadingText}>Loading event…</p>
          </div>
        </div>
      </div>
    );
  }

  const isDraft = !event || event.status === 'draft';
  const isPublished = event?.status === 'published';

  return (
    <div className={styles.page}>
      <div className={styles.inner}>
        <Link to="/events" className={styles.back}>
          <ArrowLeft size={14} />
          Events
        </Link>

        <div className={styles.header}>
          <h1 className={styles.title}>{isEdit ? 'Edit event' : 'New event'}</h1>
          <div className={styles.headerActions}>
            {isEdit && isDraft && (
              <button type="button" className={styles.publishBtn} onClick={handlePublish} disabled={publishing}>
                {publishing ? 'Publishing…' : 'Publish'}
              </button>
            )}
            {isPublished && event?.short_code && (
              <a
                href={`/e/${event.short_code}`}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.viewLink}
              >
                View public page →
              </a>
            )}
          </div>
        </div>

        <form className={styles.form} onSubmit={handleSave} noValidate>
          <div className={styles.field}>
            <label htmlFor="ev-title" className={styles.label}>Title *</label>
            <input
              id="ev-title"
              className={styles.input}
              type="text"
              value={form.title}
              onChange={set('title')}
              placeholder="Summer Networking Evening"
              required
            />
          </div>

          <div className={styles.field}>
            <label htmlFor="ev-description" className={styles.label}>Description</label>
            <textarea
              id="ev-description"
              className={styles.textarea}
              value={form.description}
              onChange={set('description')}
              placeholder="Tell attendees what to expect…"
              rows={4}
            />
          </div>

          <div className={styles.field}>
            <label htmlFor="ev-location" className={styles.label}>Location</label>
            <input
              ref={locationInputRef}
              id="ev-location"
              className={styles.input}
              type="text"
              value={form.location}
              onChange={set('location')}
              placeholder="Start typing a venue or address…"
              autoComplete="off"
            />
          </div>

          <div className={styles.row}>
            <div className={styles.field}>
              <label htmlFor="ev-starts" className={styles.label}>Starts *</label>
              <input
                id="ev-starts"
                className={styles.input}
                type="datetime-local"
                value={form.starts_at}
                onChange={set('starts_at')}
                required
              />
            </div>
            <div className={styles.field}>
              <label htmlFor="ev-ends" className={styles.label}>Ends *</label>
              <input
                id="ev-ends"
                className={styles.input}
                type="datetime-local"
                value={form.ends_at}
                onChange={set('ends_at')}
                required
              />
            </div>
          </div>

          <div className={styles.field}>
            <label htmlFor="ev-capacity" className={styles.label}>Capacity *</label>
            <input
              id="ev-capacity"
              className={styles.input}
              type="number"
              min="1"
              value={form.capacity}
              onChange={set('capacity')}
              placeholder="100"
              required
            />
          </div>

          <div className={styles.checkboxRow}>
            <input
              id="ev-is-free"
              type="checkbox"
              className={styles.checkbox}
              checked={form.is_free}
              onChange={setCheck('is_free')}
            />
            <label htmlFor="ev-is-free" className={styles.checkboxLabel}>Free event</label>
          </div>

          {!form.is_free && (
            <div className={styles.field}>
              <label htmlFor="ev-price" className={styles.label}>Price (pence) *</label>
              <input
                id="ev-price"
                className={styles.input}
                type="number"
                min="1"
                value={form.price_pence}
                onChange={set('price_pence')}
                placeholder="1000 = £10.00"
                required
              />
              {form.price_pence && Number(form.price_pence) > 0 && (
                <span className={styles.hint}>
                  £{(Number(form.price_pence) / 100).toFixed(2)}
                </span>
              )}
            </div>
          )}

          {validationError && (
            <div className={styles.errorBox} role="alert">
              <AlertCircle size={16} className={styles.errorIcon} />
              <p className={styles.errorText}>{validationError}</p>
            </div>
          )}

          <div className={styles.actions}>
            <button type="button" className={styles.cancelBtn} onClick={() => navigate('/events')}>
              Cancel
            </button>
            <button type="submit" className={styles.saveBtn} disabled={saving}>
              {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create event'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
